import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import {
  GraduationCap, UsersThree, Globe, Sparkle, Heart, ArrowRight,
  Buildings, ClockCounterClockwise, MagnifyingGlass, ChatsCircle, CalendarStar, Compass,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import SampleLinks from "@/components/SampleLinks";

const SECTIONS = [
  {
    icon: Buildings,
    heading: "A Living Digital Home for Every Institution",
    body: [
      "Every educational institution deserves more than a static listing or an old website that disappears into the internet.",
      "AlumniPulse can provide a place where an institution can build and preserve its digital identity, history, community, events, alumni groups, achievements, traditions, photographs, and stories.",
      "Schools, colleges, universities, departments, campuses, clubs, and student organizations can create communities that continue to grow long after students leave the classroom.",
    ],
  },
  {
    icon: ClockCounterClockwise,
    heading: "Preserve the Educational Journey",
    body: [
      "A person's education can span decades and many places.",
      "AlumniPulse helps individuals create a complete education timeline — from kindergarten and primary school through high school, college, university, graduate school, professional programs, and beyond.",
      "Record the institutions, campuses, departments, courses, classes, batches, teachers, classmates, achievements, and memories that shaped your journey.",
      "Over time, this becomes more than a résumé. It becomes a personal history of where you learned, who you met, and how you grew.",
    ],
  },
  {
    icon: MagnifyingGlass,
    heading: "Reconnect With the People Who Shared Your Journey",
    body: [
      "How many classmates have you lost touch with?",
      "AlumniPulse provides tools to help people find classmates, discover alumni communities, join groups, send messages, participate in discussions, and reconnect with old friends and colleagues.",
      "A graduation ceremony may mark the end of a school chapter — but it doesn't have to mark the end of the relationships created there.",
    ],
  },
  {
    icon: ChatsCircle,
    heading: "Communities That Stay Alive",
    body: [
      "Every class, batch, department, and alumni community can have a place to stay connected.",
      "Create alumni groups, persistent community chats, reunions, events, discussions, and shared memories. Members can continue conversations, organize gatherings, celebrate achievements, support one another, and welcome new generations of alumni.",
      "The goal isn't simply to find old classmates. It's to keep the community alive.",
    ],
  },
  {
    icon: CalendarStar,
    heading: "From Reunions to Lifelong Connections",
    body: [
      "AlumniPulse makes it easier to organize the moments that bring people back together.",
      "Create and manage reunions, alumni gatherings, school events, university events, networking events, clubs, student activities, and community celebrations. Manage RSVPs and guest lists, share virtual meeting links, communicate with attendees, and preserve photographs and memories afterward.",
      "An event can last a day. The memories can last forever.",
    ],
  },
  {
    icon: Compass,
    heading: "A Global Directory of Education",
    body: [
      "We're building a worldwide directory of educational institutions so that people can discover the places that shaped their lives.",
      "Find schools, colleges, universities, campuses, departments, and programs — and discover the alumni communities connected to them.",
      "Can't find your institution? Suggest it. Help us build a more complete picture of education around the world, one institution at a time.",
    ],
  },
];

const GENERATIONS = [
  "The student who just completed their first school year.",
  "The graduate beginning a career.",
  "The parent remembering their college days.",
  "The teacher who influenced generations of students.",
  "The alumnus searching for a childhood friend.",
  "The institution preserving a century of history.",
  "And the community coming together for its next reunion.",
];

const VALUES = [
  { slug: "global-by-design", icon: Globe, title: "Global by design", to: "/articles/global-by-design",
    body: "A single home for every school, college and university in the world — and the people who passed through them." },
  { slug: "people-first", icon: UsersThree, title: "People first", to: "/articles/people-first",
    body: "We help you find classmates, teachers and lifelong friends, with privacy you control at every step." },
  { slug: "ai-that-assists", icon: Sparkle, title: "AI that assists, never invents", to: "/articles/ai-that-assists-never-invents",
    body: "From website copy to translations, our AI drafts — you approve. Facts stay yours." },
  { slug: "memories-that-last", icon: Heart, title: "Memories that last", to: "/articles/memories-that-last",
    body: "Stories, photos and reunions preserved for the generations who come after." },
];

export default function About() {
  useSeo("About", "AlumniPulse is the digital home for educational institutions and their alumni worldwide.");
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="mx-auto max-w-4xl px-5 py-20 text-center">
        <p className="overline text-secondary mb-4">The Digital Home for Institutions &amp; Their Alumni</p>
        <h1 className="font-heading text-4xl font-light leading-tight tracking-tight sm:text-5xl">
          Every school has a story. Every class has memories. <span className="italic font-medium text-secondary">Every alumnus has a connection.</span>
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          AlumniPulse.net is a global platform built to help educational institutions and their alumni stay connected across generations — from a small village primary school to a major university with alumni around the world.
        </p>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          Education doesn't end when a student graduates. The friendships, experiences, teachers, achievements, traditions, and memories created along the way can remain part of a person's life forever. AlumniPulse is designed to preserve that journey and turn those lifelong connections into a living, growing community.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/register"><Button size="lg" className="rounded-full gap-2 px-7" data-testid="about-join-btn">Join AlumniPulse™ <ArrowRight size={18} /></Button></Link>
          <Link to="/explore/institutions"><Button size="lg" variant="outline" className="rounded-full px-7" data-testid="about-browse-btn">Browse institutions</Button></Link>
        </div>
      </section>

      {/* Feature image band */}
      <section className="mx-auto max-w-7xl px-5 pb-4">
        <SampleLinks className="mb-6 justify-center" />
        <div className="relative overflow-hidden rounded-[2rem] border border-border/60">
          <img
            src="https://images.unsplash.com/photo-1517457373958-b7bdd4587205?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Alumni community gathered together at a reunion"
            className="h-[280px] w-full object-cover sm:h-[380px]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
          <p className="absolute bottom-6 left-6 right-6 font-heading text-xl font-light text-white sm:text-2xl">
            Turning lifelong connections into a living, growing community.
          </p>
        </div>
      </section>

      {/* Narrative sections */}
      <section className="mx-auto max-w-4xl px-5 py-16">
        <div className="space-y-14">
          {SECTIONS.map((s) => (
            <div key={s.heading} className="grid gap-4 sm:grid-cols-[auto_1fr] sm:gap-6" data-testid="about-section">
              <span className="inline-grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-accent text-primary">
                <s.icon size={24} weight="duotone" />
              </span>
              <div>
                <h2 className="font-heading text-2xl font-light tracking-tight sm:text-3xl">{s.heading}</h2>
                <div className="mt-3 space-y-3">
                  {s.body.map((p, i) => (
                    <p key={i} className="leading-relaxed text-muted-foreground">{p}</p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Your Education. Your People. Your Story. */}
      <section className="mx-auto max-w-7xl px-5 pb-4">
        <div className="rounded-[2rem] border border-border/60 bg-muted/30 p-8 sm:p-12">
          <h2 className="font-heading text-3xl font-light tracking-tight">Your Education. Your People. Your Story.</h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            AlumniPulse brings together the different pieces of an educational and professional journey — education timelines, alumni groups, classmates, institutions, events, reunions, memories, resumes, and professional connections.
          </p>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            Your profile can become a living record of your journey — something you can continue building throughout your life. And with shareable profiles, resumes, short links, and QR codes, your AlumniPulse identity can travel with you wherever your journey takes you.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Education timelines", "Alumni groups", "Classmates", "Institutions", "Events", "Reunions", "Memories", "Resumes", "Professional connections"].map((t) => (
              <span key={t} className="rounded-full border border-border/60 bg-card px-4 py-1.5 text-sm text-foreground/80">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Every Generation */}
      <section className="mx-auto max-w-4xl px-5 py-16">
        <div className="text-center">
          <span className="mb-5 inline-grid h-12 w-12 place-items-center rounded-2xl bg-accent text-primary"><UsersThree size={24} weight="duotone" /></span>
          <h2 className="font-heading text-3xl font-light tracking-tight">Built for Every Generation</h2>
          <p className="mt-3 text-muted-foreground">AlumniPulse isn't only for recent graduates. It's for:</p>
        </div>
        <ul className="mx-auto mt-8 grid max-w-2xl gap-3 sm:grid-cols-2">
          {GENERATIONS.map((g) => (
            <li key={g} className="flex items-start gap-3 rounded-xl border border-border/60 bg-card p-4 text-sm leading-relaxed text-foreground/85">
              <Heart size={18} weight="fill" className="mt-0.5 shrink-0 text-secondary" /> {g}
            </li>
          ))}
        </ul>
        <p className="mt-8 text-center font-heading text-lg font-light text-secondary">Every generation has a place here.</p>
      </section>

      {/* What we believe — linked value boxes */}
      <section className="mx-auto max-w-7xl px-5 pb-4">
        <div className="mb-8 text-center">
          <p className="overline text-secondary mb-2">What we believe</p>
          <h2 className="font-heading text-3xl font-light tracking-tight">Four ideas at the heart of AlumniPulse.</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v) => (
            <Link key={v.slug} to={v.to} data-testid={`about-value-${v.slug}`} className="group flex flex-col rounded-2xl border border-border/60 bg-card p-7 shadow-sm transition-colors hover:border-secondary/40">
              <span className="mb-5 inline-grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary"><v.icon size={24} weight="duotone" /></span>
              <h3 className="font-heading text-lg font-medium">{v.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-secondary">Read more <ArrowRight size={14} /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* Founder spotlight */}
      <section className="mx-auto max-w-4xl px-5 pb-4" data-testid="about-founder">
        <Link to="/founder" className="group flex flex-col items-center gap-6 rounded-[2rem] border border-border/60 bg-card p-8 text-center shadow-sm transition-colors hover:border-secondary/40 sm:flex-row sm:text-left">
          <img src="/founder/portrait.jpg" alt="Tom (Thomas) Vellaringattu, Founder of AlumniPulse" className="h-24 w-24 shrink-0 rounded-2xl object-cover sm:h-28 sm:w-28" data-testid="about-founder-photo" />
          <div>
            <p className="overline text-secondary mb-1">Meet the founder</p>
            <p className="font-heading text-xl font-light italic text-primary">“Imagine beyond what you know. Learn what you need. Build what you imagine.”</p>
            <p className="mt-2 text-sm text-muted-foreground">Tom (Thomas) Vellaringattu — Founder of AlumniPulse</p>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-secondary">Read his story <ArrowRight size={14} /></span>
          </div>
        </Link>
      </section>

      {/* Connecting Past, Present & Future + closing */}
      <section className="mx-auto max-w-7xl px-5 pb-24">
        <div className="grid items-center gap-10 rounded-[2rem] border border-border/60 bg-primary/[0.03] p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <p className="overline text-secondary mb-3">Connecting the Past, Present &amp; Future</p>
            <h2 className="font-heading text-3xl font-light tracking-tight">Remember where you came from.</h2>
            <p className="mt-4 text-muted-foreground">
              We believe the value of an educational institution is measured not only by the students it teaches, but also by the community and legacy it creates. AlumniPulse.net is being built to help preserve that legacy while creating new opportunities for connection.
            </p>
            <p className="mt-3 text-muted-foreground">
              Reconnect with the people who shared your journey. Celebrate where you are today. And help build the community for those who come next.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register"><Button size="lg" className="rounded-full gap-2 px-7">Join AlumniPulse™ <ArrowRight size={18} /></Button></Link>
              <Link to="/countries"><Button size="lg" variant="outline" className="rounded-full gap-2 px-7">Explore by country <Globe size={18} /></Button></Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 -rotate-2 rounded-[2rem] bg-secondary/10" />
            <img src="https://images.pexels.com/photos/8199169/pexels-photo-8199169.jpeg" alt="Graduates celebrating together" className="relative aspect-[4/3] w-full rounded-[1.75rem] object-cover shadow-xl" />
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="mx-auto inline-flex items-center gap-2 text-secondary"><GraduationCap size={26} weight="duotone" /><Sparkle size={18} weight="fill" /></div>
          <p className="mt-3 font-heading text-2xl font-light tracking-tight">AlumniPulse.net</p>
          <p className="mt-1 text-muted-foreground">The digital home for institutions and their alumni.</p>
          <p className="mt-3 font-heading text-lg font-medium text-secondary">Connect. Reconnect. Preserve. Grow.</p>
        </div>
      </section>
    </div>
  );
}
